"use client";

import { Button } from "@/components/ui/Button";
import { sbLink } from "@/lib/storyblok/link";
import type { BlokButton } from "@/storyblok";
import type { BlokProps } from "@frend-digital/storyblok";
import BlokWrap from "../BlokWrap";

import { SegmentControl } from "@/components/SegmentControl";
import { useGlobal } from "@/features/global/GlobalContext";
import { trackContentClick } from "@/lib/analytics/trackContentClick";
import { clsx } from "@frend-digital/ui";
import type { StoryblokMultilink } from "storyblok";
import styles from "./ButtonBlok.module.css";

export const ButtonBlok = ({
	blok,
	className,
	buttonColor,
	trackingContext,
}: BlokProps<BlokButton> & {
	className?: string;
	buttonColor?: string;
	// Set by a parent blok (e.g. BannerBlok) so the click event identifies
	// the banner, not this nested button, as the clicked content.
	trackingContext?: { type: string; id: string };
}) => {
	const {
		data: { colorMap },
	} = useGlobal();

	const colorEntry = colorMap[blok.color! || buttonColor || ""] || "";
	const link = sbLink(blok.link as StoryblokMultilink);

	return (
		<SegmentControl segments={blok}>
			<BlokWrap editable={blok} as="div" className={clsx(styles.root, className, "button")} asChild>
				<Button
					href={link?.href}
					onClick={
						link?.href
							? () =>
									trackContentClick({
										contentType: trackingContext?.type ?? blok.component,
										itemId: trackingContext?.id ?? blok._uid,
										href: link.href,
										label: blok.text,
									})
							: undefined
					}
					{...((link?.external || link?.target === "_blank") && {
						target: "_blank",
						rel: "noopener noreferrer",
					})}
					style={
						{
							"--button-bg": colorEntry.color,
							"--button-text": colorEntry.foreground,
						} as React.CSSProperties
					}
					className={styles.button}
					{...(blok.outline && {
						"data-outline": "true",
						"data-outline-color": blok.outlineColor || "white",
					})}
				>
					{blok.text}
				</Button>
			</BlokWrap>
		</SegmentControl>
	);
};
